/* Studio Lostit V2 — progressive infinite scrolling for Shopify's native
   collection, search, collection-directory and blog pagination.
   Unpublished V2 only. Does NOT touch custom paged video-library navigation.
   Server links remain in the HTML for crawlers and JavaScript-free visitors. */
(() => {
  'use strict';
  if (!('IntersectionObserver' in window) || !('fetch' in window) || !('DOMParser' in window)) return;
  // Shopify's draft theme editor is a valid preview: do NOT disable the
  // auto-loader when Shopify.designMode is true.

  const attached = new WeakSet();
  const currentPath = window.location.pathname;
  const contexts = {
    collection: { selector: '#ProductGridContainer .collection', grid: 'ul#product-grid' },
    search: { selector: '#ProductGridContainer .template-search__results', grid: 'ul.product-grid' },
    directory: { selector: 'main .collection-list', grid: 'ul.collection-list' },
    blog: { selector: '.main-blog', grid: '.blog-articles' },
  };

  function contextFor(wrapper) {
    let root;
    if (wrapper.closest('#ProductGridContainer')) {
      root = wrapper.closest('.template-search__results');
      if (root) return { kind: 'search', root, grid: root.querySelector(contexts.search.grid) };
      root = wrapper.closest('.collection');
      if (root) return { kind: 'collection', root, grid: root.querySelector(contexts.collection.grid) };
    }
    root = wrapper.closest('.main-blog');
    if (root) return { kind: 'blog', root, grid: root.querySelector(contexts.blog.grid) };
    root = wrapper.parentElement;
    if (root && root.querySelector(':scope > ul.collection-list')) {
      return { kind: 'directory', root, grid: root.querySelector('ul.collection-list') };
    }
    return null;
  }

  function scopeInDocument(doc, kind) {
    if (kind === 'directory') {
      const list = doc.querySelector(contexts.directory.selector);
      return list ? list.parentElement : null;
    }
    return doc.querySelector(contexts[kind].selector);
  }

  function nextURL(wrapper) {
    const link = wrapper && wrapper.querySelector('a.pagination__item--prev[href]');
    if (!link) return null; // In Dawn, --prev is the right arrow for the NEXT page.
    let url;
    try { url = new URL(link.getAttribute('href'), window.location.href); } catch (_) { return null; }
    if (url.origin !== window.location.origin || url.pathname !== currentPath) return null;

    // Shopify's pagination links can omit the V2 theme preview query value.
    // Preserve the current search/filter/sort/view and theme-preview parameters.
    const active = new URL(window.location.href);
    active.searchParams.forEach((value, key) => {
      if (!/^page(?:_|$)/.test(key) && key !== 'section_id' && !url.searchParams.has(key)) {
        url.searchParams.append(key, value);
      }
    });
    url.hash = '';
    if (!Array.from(url.searchParams.keys()).some((key) => /^page(?:_|$)/.test(key))) return null;
    return url;
  }

  function revealCards(node) {
    node.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.add('scroll-trigger--cancel');
      el.classList.remove('animate--slide-in', 'animate--fade-in');
      el.removeAttribute('data-cascade');
      el.style.removeProperty('--animation-order');
    });
    if (node.classList.contains('scroll-trigger')) {
      node.classList.add('scroll-trigger--cancel');
      node.classList.remove('animate--slide-in', 'animate--fade-in');
      node.removeAttribute('data-cascade');
      node.style.removeProperty('--animation-order');
    }
  }

  function enhance(wrapper) {
    if (attached.has(wrapper)) return;
    const context = contextFor(wrapper);
    if (!context || !context.grid || !context.grid.children.length) return;
    attached.add(wrapper);
    const { kind, grid } = context;
    const status = document.createElement('div');
    status.className = 'sls-v2-infinite-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.style.cssText = 'min-height:1px;text-align:center;margin:1.5rem auto;font-size:1.35rem;';
    wrapper.insertAdjacentElement('afterend', status);

    let next = nextURL(wrapper);
    const visited = new Set([window.location.href]);
    let busy = false;
    let ended = !next;
    let observer;

    const showNativeFallback = () => {
      wrapper.style.display = '';
      if (observer) observer.disconnect();
    };

    const appendNext = async () => {
      if (busy || ended || !next) return;
      if (!wrapper.isConnected || !grid.isConnected) return;
      const requested = next;
      if (visited.has(requested.href)) {
        ended = true;
        status.textContent = '';
        return;
      }
      busy = true;
      status.textContent = 'Loading more items…';
      try {
        const response = await fetch(requested.href, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Next page unavailable');
        if (!wrapper.isConnected || !grid.isConnected) return;
        const fetched = new DOMParser().parseFromString(await response.text(), 'text/html');
        const found = scopeInDocument(fetched, kind);
        const newGrid = found && found.querySelector(contexts[kind].grid);
        if (!newGrid || !newGrid.children.length) throw new Error('No matching items');
        if (!wrapper.isConnected || !grid.isConnected) return;

        // Search can switch between product-only and mixed result pages.
        if (kind === 'search' && !newGrid.classList.contains('sls-v2-product-only-results')) {
          grid.classList.remove('sls-v2-product-only-results');
        }
        const fragment = document.createDocumentFragment();
        const added = [];
        Array.from(newGrid.children).forEach((entry) => {
          const card = document.importNode(entry, true);
          revealCards(card);
          fragment.appendChild(card);
          added.push(card);
        });
        grid.appendChild(fragment); // Native custom elements & quick-add connect normally.
        visited.add(requested.href);
        next = nextURL(found.querySelector('.pagination-wrapper'));
        if (next && visited.has(next.href)) next = null;
        ended = !next;
        status.textContent = '';
        document.dispatchEvent(new CustomEvent('sls-v2:infinite-items-added', {
          detail: { kind, count: added.length, page: requested.searchParams.get('page') },
        }));
        if (ended && observer) observer.disconnect();
      } catch (_) {
        ended = true; // Stop automatic retries after a failed request.
        status.textContent = 'More items could not load automatically. Use the next-page link below.';
        showNativeFallback(); // No invisible dead end if Shopify/network fails.
      } finally {
        busy = false;
        // Short result pages may leave the sentinel in view after appending.
        // Continue incrementally until the visitor has room to scroll.
        if (!ended && next && wrapper.isConnected && grid.isConnected) {
          requestAnimationFrame(() => {
            const box = status.getBoundingClientRect();
            if (box.top <= window.innerHeight + 750 && box.bottom >= -750) appendNext();
          });
        }
      }
    };

    if (!next) {
      wrapper.style.display = 'none';
      status.textContent = '';
      return;
    }
    observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) appendNext();
    }, { rootMargin: '750px 0px', threshold: 0 });
    wrapper.style.display = 'none';
    document.documentElement.dataset.slsInfiniteScroll = 'active';
    observer.observe(status);
    // Fallback for dynamically resized content and browsers that delay
    // observer callbacks inside the Shopify theme preview/editor.
    const checkWhileScrolling = () => {
      if (busy || ended || !status.isConnected) return;
      const bounds = status.getBoundingClientRect();
      if (bounds.top < window.innerHeight + 900 && bounds.bottom > -200) appendNext();
    };
    window.addEventListener('scroll', checkWhileScrolling, { passive: true });
    window.addEventListener('resize', checkWhileScrolling, { passive: true });
    requestAnimationFrame(checkWhileScrolling);
  }

  function enhanceAll() {
    document.querySelectorAll('.pagination-wrapper').forEach(enhance);
  }

  function start() {
    enhanceAll();
    // Shopify facets replace #ProductGridContainer with a new paginated grid.
    // Reattach to the new navigation; the old grid is never appended to.
    const facetContainer = document.getElementById('ProductGridContainer');
    if (facetContainer) {
      let queued = false;
      new MutationObserver(() => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          enhanceAll();
        });
      }).observe(facetContainer, { subtree: true, childList: true });
    }
    document.addEventListener('shopify:section:load', enhanceAll);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();